import useSWR from 'swr';
import { Link } from 'react-router-dom';
import PoliticianCard from '../components/PoliticianCard';
import { fetcher } from '../services/api';

export default function HomePage() {
  // Fetch all data
  const { data: stats } = useSWR('stats', fetcher);
  const { data: topPoliticians, isLoading: politiciansLoading } = useSWR('politicians?per_page=6', fetcher);
  const { data: parties } = useSWR('parties', fetcher);
  const { data: initiatives } = useSWR('initiatives?limit=5', fetcher);

  const topPoliticianList = topPoliticians?.data || [];
  const partiesData = parties?.data || [];
  const initiativesData = initiatives?.data || [];
  const statsData = stats?.data;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Hero Section with NLP Search */}
      <section className="mb-12">
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 md:p-12 text-white">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Transparencia Política en México
          </h1>
          <p className="text-lg text-primary-100 mb-6 max-w-2xl">
            SEPOM te permite monitorear la actividad legislativa, asistencia, y desempeño de políticos en México. 
            Haz preguntas en lenguaje natural y obtén respuestas basadas en datos verificados.
          </p>

          {/* NLP Search Box */}
          <div className="max-w-2xl">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const query = formData.get('q') as string;
                if (query) {
                  window.location.href = `/buscar?q=${encodeURIComponent(query)}`;
                }
              }}
            >
              <div className="flex">
                <input
                  type="text"
                  name="q"
                  placeholder="¿Qué senadores han faltado más? ¿Qué partidos han propuesto más leyes ambientales?"
                  className="flex-1 px-4 py-3 rounded-l-lg text-gray-900 focus:outline-none"
                />
                <button
                  type="submit"
                  className="bg-white text-primary-600 px-6 py-3 rounded-r-lg font-semibold hover:bg-primary-50 transition-colors"
                >
                  Buscar
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Key Statistics Dashboard */}
      {statsData && (
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Panorama General</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="card">
              <div className="text-3xl font-bold text-primary-600">{statsData.overview.total_politicians}</div>
              <div className="text-sm text-gray-500">Representantes monitoreados</div>
              <div className="text-xs text-gray-400 mt-2">{statsData.overview.total_parties} partidos en {statsData.overview.total_states} estados</div>
            </div>

            <div className="card">
              <div className="text-3xl font-bold text-green-600">{Math.round(statsData.overview.avg_attendance)}%</div>
              <div className="text-sm text-gray-500">Asistencia promedio</div>
              <div className="text-xs text-gray-400 mt-2">{statsData.overview.total_sessions} sesiones registradas</div>
            </div>

            <div className="card">
              <div className="text-3xl font-bold text-blue-600">{statsData.overview.total_initiatives}</div>
              <div className="text-sm text-gray-500">Iniciativas propuestas</div>
              <div className="text-xs text-gray-400 mt-2">{statsData.overview.approved_initiatives} aprobadas</div>
            </div>

            <div className="card">
              <div className="text-3xl font-bold text-purple-600">↑ {statsData.trends.legislative_activity}%</div>
              <div className="text-sm text-gray-500">Tendencia legislativa</div>
              <div className="text-xs text-gray-400 mt-2">Últimos 30 días</div>
            </div>
          </div>
        </section>
      )}

      {/* Party Distribution */}
      {statsData && statsData.parties && (
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Distribución por Partido</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {statsData.parties.map((party: any) => (
              <Link
                key={party.party_abbrev}
                to={`/partido/${party.party_id}`}
                className="card-hover text-center py-6"
              >
                <div
                  className="w-16 h-16 mx-auto rounded-full mb-3 flex items-center justify-center text-white font-bold text-lg"
                  style={{ backgroundColor: party.color_hex || '#6B7280' }}
                >
                  {party.party_abbrev}
                </div>
                <p className="font-medium text-gray-900 text-sm">{party.party_name}</p>
                <p className="text-xs text-gray-500 mt-1">{party.politician_count} representantes</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Recent Initiatives */}
      {initiativesData && initiativesData.length > 0 && (
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Iniciativas Recientes</h2>
            <Link to="/buscar" className="text-primary-600 hover:text-primary-700 font-medium text-sm">
              Ver todas →
            </Link>
          </div>

          <div className="space-y-3">
            {initiativesData.slice(0, 5).map((initiative: any) => (
              <div key={initiative.id} className="card p-4 hover:bg-gray-50 transition-colors">
                <h3 className="font-semibold text-gray-900">{initiative.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{initiative.description}</p>
                <div className="mt-2 flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                  <span>Por: <strong>{initiative.politician_name}</strong></span>
                  <span className={`px-2 py-1 rounded ${
                    initiative.status === 'approved' ? 'bg-green-100 text-green-700' :
                    initiative.status === 'debated' ? 'bg-blue-100 text-blue-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {initiative.status === 'approved' ? 'Aprobada' : 
                     initiative.status === 'debated' ? 'En debate' : 'Pendiente'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Top Representatives */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Representantes Destacados</h2>
          <Link to="/buscar" className="text-primary-600 hover:text-primary-700 font-medium">
            Ver todos →
          </Link>
        </div>

        {politiciansLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-40 bg-gray-200 rounded-lg" />
              </div>
            ))}
          </div>
        ) : topPoliticianList.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {topPoliticianList.map((p: any) => (
              <PoliticianCard
                key={p.id}
                id={p.id}
                name={p.full_name}
                photoUrl={p.photo_url}
                partyName={p.party_name}
                partyAbbrev={p.party_abbrev}
                partyColor={p.party_color}
                positionTitle={p.position_title}
                stateName={p.state_name}
                rating={p.rating || 75}
                stats={{
                  initiatives: p.stats?.initiatives || 0,
                  attendance: p.attendance?.percentage || 0,
                  approvals: 0,
                  speeches: 0,
                }}
              />
            ))}
          </div>
        ) : (
          <div className="py-20 text-center text-gray-500">Cargando representantes...</div>
        )}
      </section>

      {/* All Parties */}
      {partiesData.length > 0 && (
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Todos los Partidos</h2>
            <Link to="/buscar?tipo=partidos" className="text-primary-600 hover:text-primary-700 font-medium text-sm">
              Análisis por partido →
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {partiesData.map((party: any) => (
              <Link
                key={party.id}
                to={`/partido/${party.id}`}
                className="card-hover text-center py-6"
              >
                <div
                  className="w-16 h-16 mx-auto rounded-full mb-3 flex items-center justify-center text-white font-bold text-xl"
                  style={{ backgroundColor: party.color_hex || '#6B7280' }}
                >
                  {party.abbreviation}
                </div>
                <p className="font-medium text-gray-900 text-sm">{party.name}</p>
                <p className="text-xs text-gray-500 mt-1">{party.politician_count || 0} representantes</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Call to Action */}
      <section>
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 text-center border border-blue-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">¿Quieres explorar más?</h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Accede a perfiles completos, compara legisladores, monitorea asistencia, y análisis IA en tiempo real.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link
              to="/buscar"
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              Explorar Representantes
            </Link>
            <Link
              to="/buscar?tipo=partidos"
              className="px-6 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors font-medium"
            >
              Analizar Partidos
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
